import { useRef, useState, useCallback, useEffect } from "react";
import type { Editor } from "@tiptap/react";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import { EditorRefApi } from "shared/components/Editor";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { LiteTextEditor } from "shared/app/Editor/LiteTextEditor";
import {
  CreateNotePayload,
  EditNotePayload,
  NoteFilePayload
} from "../../types";
import { extractEntitiesFromJson, isTextEmpty } from "../../helpers";
import { cx } from "shared/utils/classnames";
import { getPresignedUrl, uploadFileToS3Bucket } from "utils/fileHandlers";
import { toast } from "react-toastify";
import {
  NoteAttachmentCard,
  type PendingAttachment
} from "./NoteAttachmentCard";
import { NoteToolbar } from "./NoteToolbar";
import { NoteLinkModal, type NoteLinkModalState } from "./NoteLinkModal";

const noteExtensions = [
  Link.extend({ inclusive: false }).configure({
    openOnClick: false,
    enableClickSelection: true,
    HTMLAttributes: {
      class: "text-primary-500 font-semibold"
    }
  }),
  Underline
];
const MAX_ATTACHMENT_SIZE_BYTES = 25 * 1024 * 1024;
const ALLOWED_ATTACHMENT_TYPES = /image\/.*|application\/pdf/;

const defaultLinkModalState: NoteLinkModalState = {
  isOpen: false,
  mode: "add",
  initialText: "",
  initialUrl: "",
  savedSelection: null,
  savedText: ""
};

interface NoteFormProps {
  onSubmit: (data: CreateNotePayload | EditNotePayload) => Promise<void>;
  onCancel?: () => void;
  initialValue?: string;
  existingAttachments?: number[];
  applicationId?: string;
  propertyName?: string;
  isLoading?: boolean;
  editorContainerClassName?: string;
}

export const NoteForm = ({
  onSubmit,
  onCancel,
  initialValue,
  existingAttachments,
  applicationId,
  propertyName,
  isLoading = false,
  editorContainerClassName
}: NoteFormProps) => {
  const editorRef = useRef<EditorRefApi>(null);
  const [isEmpty, setIsEmpty] = useState(() => isTextEmpty(initialValue));
  const [editor, setEditor] = useState<Editor | null>(null);
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [linkModalState, setLinkModalState] = useState<NoteLinkModalState>(
    defaultLinkModalState
  );
  const dragCounter = useRef(0);

  const handleFileUpload = useCallback(async (files: FileList) => {
    const validFiles = Array.from(files).filter((file) => {
      if (!ALLOWED_ATTACHMENT_TYPES.test(file.type)) {
        toast.error(
          "The file format is not valid. Please upload a PDF, IMAGE file."
        );
        return false;
      }

      if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
        toast.error(
          <>
            <p className="font-bold text-sm">{`${file.name} could not be uploaded`}</p>
            <p className="text-sm">Maximum file size allowed: 25 MB</p>
          </>,
          {
            style: { width: "400px" }
          }
        );
        return false;
      }

      return true;
    });

    if (!validFiles.length) return;

    const newAttachments: PendingAttachment[] = validFiles.map((file) => ({
      id: `${file.name}-${Date.now()}`,
      file,
      status: "uploading" as const
    }));

    setAttachments((prev) => [...prev, ...newAttachments]);

    await Promise.all(
      newAttachments.map((attachment) => uploadSingleFile(attachment))
    );
  }, []);

  const uploadSingleFile = useCallback(
    async (attachment: PendingAttachment) => {
      try {
        const presignedUrl = await getPresignedUrl(attachment.file);
        await uploadFileToS3Bucket(presignedUrl, attachment.file);
        setAttachments((prev) =>
          prev.map((a) =>
            a.id === attachment.id
              ? { ...a, status: "uploaded", uploadUrl: presignedUrl }
              : a
          )
        );
      } catch {
        toast.error(`Failed to upload ${attachment.file.name}`);
        setAttachments((prev) =>
          prev.map((a) =>
            a.id === attachment.id ? { ...a, status: "error" } : a
          )
        );
      }
    },
    []
  );

  const handleRemoveAttachment = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const handleSubmit = async () => {
    const doc = editorRef.current?.getDocument();
    if (!doc) return;

    const entities = extractEntitiesFromJson(doc.json);
    const uploadedAttachments: NoteFilePayload[] = attachments
      .filter((a) => a.status === "uploaded" && a.uploadUrl)
      .map((a) => ({
        url: a.uploadUrl as string,
        metadata: {
          size: a.file.size,
          original_filename: a.file.name,
          content_type: a.file.type
        }
      }));

    const basePayload = {
      message: doc.text,
      document_html: doc.html,
      entities
    };

    const value: CreateNotePayload | EditNotePayload = existingAttachments
      ? {
          ...basePayload,
          existing_attachment_ids: existingAttachments,
          ...(uploadedAttachments.length > 0 && {
            new_attachments: uploadedAttachments
          })
        }
      : {
          ...basePayload,
          ...(uploadedAttachments.length > 0 && {
            attachments: uploadedAttachments
          })
        };

    await onSubmit(value);
    editorRef.current?.clearEditor();
    setIsEmpty(true);
    setAttachments([]);
  };

  const hasContent = !isEmpty || attachments.length > 0;

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current += 1;
    if (e.dataTransfer.types.includes("Files")) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDragging(false);
    if (e.dataTransfer.files.length) handleFileUpload(e.dataTransfer.files);
  };

  const closeLinkModal = () => {
    setLinkModalState((prev) => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    if (!editor) return;

    const handleEditorClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const anchorEl = target?.closest("a");
      if (!anchorEl) return;

      event.preventDefault();
      requestAnimationFrame(() => {
        const selection = editor.state.selection;
        const selectedText = editor.state.doc.textBetween(
          selection.from,
          selection.to,
          ""
        );
        const href =
          editor.getAttributes("link").href ??
          anchorEl.getAttribute("href") ??
          "";
        setLinkModalState({
          isOpen: true,
          mode: "view",
          initialText: selectedText,
          initialUrl: href,
          savedSelection: selection,
          savedText: selectedText
        });
      });
    };

    const editorDom = editor.view.dom;
    editorDom.addEventListener("click", handleEditorClick);

    return () => {
      editorDom.removeEventListener("click", handleEditorClick);
    };
  }, [editor]);

  return (
    <div className="space-y-3">
      <div
        onDragEnter={handleDragEnter}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cx(
          "relative rounded-lg border shadow-xs overflow-hidden transition-colors",
          isDragging
            ? "border-alpha-10 bg-background-900 shadow-xs"
            : "border-alpha-100",
          editorContainerClassName
        )}
      >
        <div className="max-h-48 overflow-y-auto">
          <LiteTextEditor
            applicationId={applicationId}
            propertyName={propertyName}
            ref={editorRef}
            id="note-editor"
            editable
            initialValue={initialValue}
            placeholder="You can write a note here"
            extensions={noteExtensions}
            containerClassName="py-3 px-4 text-base font-medium min-h-[80px]"
            onEditorMount={setEditor}
            onChange={() => {
              const doc = editorRef.current?.getDocument();
              if (doc) setIsEmpty(isTextEmpty(doc.text));
            }}
          />

          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 px-4">
              {attachments.map((attachment) => (
                <NoteAttachmentCard
                  key={attachment.id}
                  attachment={attachment}
                  onRemove={handleRemoveAttachment}
                />
              ))}
            </div>
          )}
        </div>

        <NoteToolbar
          editor={editor}
          onFileUpload={handleFileUpload}
          onOpenLinkPopover={(args) => {
            setLinkModalState({
              isOpen: true,
              mode: args.initialUrl ? "edit" : "add",
              initialText: args.initialText,
              initialUrl: args.initialUrl,
              savedSelection: args.savedSelection,
              savedText: args.savedText
            });
          }}
        />
      </div>

      <NoteLinkModal
        editor={editor}
        state={linkModalState}
        onClose={closeLinkModal}
      />

      <Show if={hasContent}>
        <div className="flex justify-end gap-2">
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            onClick={() => {
              onCancel?.();
              editorRef.current?.clearEditor();
              setAttachments([]);
            }}
          >
            Cancel
          </Button>
          <Button
            variant={ButtonVariant.PRIMARY}
            size={ButtonSize.SM}
            disabled={
              isLoading || attachments.some((a) => a.status === "uploading")
            }
            loading={isLoading}
            onClick={handleSubmit}
          >
            Add Note
          </Button>
        </div>
      </Show>
    </div>
  );
};
